import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';

const VerifyCodePage = () => {
  const { event } = useEvent();
  const { verifyCode, sendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const email = location.state?.email;
  const action = location.state?.action; // 'login' or 'register'

  useEffect(() => {
    // Si pas d'email, retour à la page login
    if (!email) {
      navigate(`/event/${event.slug}/login`);
    } else {
      // Focus sur le premier input
      inputRefs[0].current?.focus();
    }
  }, [email, event.slug, navigate]);

  const handleChange = (index, value) => {
    // Ne garder que les chiffres
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');

    // Auto-focus sur le suivant
    if (digit && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit si les 6 chiffres sont remplis
    if (digit && index === 5 && newCode.every(d => d)) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace : retour en arrière
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs[5].current?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeString) => {
    setError('');
    setLoading(true);

    try {
      await verifyCode(email, event.id, codeString);
      
      // Vérification réussie → Redirection selon le contexte
      if (action === 'register') {
        // Nouveau compte → Onboarding
        navigate(`/event/${event.slug}/onboarding`);
      } else {
        // Login → Dashboard
        navigate(`/event/${event.slug}/dashboard`);
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setError(err.message || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const codeString = code.join('');
    
    if (codeString.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    handleVerify(codeString);
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      await sendCode(email, event.id, event.name);
      setResendSuccess(true);
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
      
      // Masquer le message de succès après 5 secondes
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      console.error('Resend code error:', err);
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/event/${event.slug}/login`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Event Logo */}
            {event.logo && (
              <div className="text-center mb-6">
                <img 
                  src={event.logo} 
                  alt={event.name}
                  className="max-h-16 object-contain mx-auto"
                />
              </div>
            )}

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h1>
              <p className="text-gray-600">
                Enter the 6-digit code sent to
              </p>
              <p className="text-blue-600 font-semibold mt-1">
                {email}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {resendSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-green-700">
                  A new code has been sent to your email
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Code Input */}
              <div className="mb-6">
                <div className="flex gap-3 justify-center">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={inputRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      disabled={loading}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || code.some(d => !d)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <span>Verify Code</span>
                )}
              </button>
            </form>

            {/* Resend Code */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResendCode}
                disabled={resendLoading}
                className="text-blue-600 font-semibold hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {resendLoading ? 'Sending...' : 'Resend Code'}
              </button>
            </div>

            {/* Help */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">💡 Tip:</span> Check your spam folder if you don't see the email
              </p>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need help? <a href="#contact" className="text-blue-600 font-semibold hover:underline">Contact support</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            Powered by <span className="font-semibold">Mobivent</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VerifyCodePage;